<template>
  <div class="tt-wrapper">
    <h1 class="text-center tt-title">Times Tables Trainer</h1>

    <!-- ── Mode bar ───────────────────────────────────────────────── -->
    <div class="mode-bar text-center">
      <button
        v-for="m in MODES"
        :key="m.id"
        class="mode-btn"
        :class="{ 'mode-btn--active': mode === m.id }"
        @click="setMode(m.id)"
      >
        {{ m.label }}
      </button>
    </div>

    <!-- ── Table picker (Practice mode only) ─────────────────────── -->
    <div v-if="mode === 'practice'" class="table-picker text-center">
      <label class="picker-label">Choose a table:</label>
      <div class="picker-grid">
        <button
          v-for="n in 12"
          :key="n"
          class="tbl-btn"
          :class="{ 'tbl-btn--active': selectedTable === n }"
          @click="selectTable(n)"
        >
          {{ n }}×
        </button>
      </div>
    </div>

    <!-- ── Random Mix description ─────────────────────────────────── -->
    <p v-if="mode === 'random'" class="mode-desc text-center">
      Questions are drawn from all 12 tables at random.
    </p>

    <!-- ── Speed Mode controls ───────────────────────────────────── -->
    <div v-if="mode === 'speed'" class="speed-controls text-center">
      <p class="mode-desc">Answer as many as you can in <strong>{{ SPEED_SECONDS }}s</strong>.</p>

      <!-- Pre-game start button -->
      <div v-if="!speedRunning && !speedDone">
        <button class="start-btn" @click="startSpeedRun">▶ Start</button>
      </div>

      <!-- In-game HUD -->
      <div v-if="speedRunning" class="speed-hud">
        <div class="hud-pill hud-timer" :class="{ 'hud-timer--low': timeLeft <= 10 }">
          ⏱ {{ timeLeft }}s
        </div>
        <div class="hud-pill hud-correct">✓ {{ speedCorrect }}</div>
        <div class="hud-pill hud-wrong">✗ {{ speedWrong }}</div>
      </div>

      <!-- Post-game results -->
      <div v-if="speedDone" class="speed-results">
        <p class="result-line">Time's up!</p>
        <div class="result-stats">
          <span class="stat-pill stat-correct">✓ {{ speedCorrect }} correct</span>
          <span class="stat-pill stat-wrong">✗ {{ speedWrong }} wrong</span>
          <span class="stat-pill stat-score">Score: {{ speedScore }}</span>
        </div>
        <button class="start-btn" @click="startSpeedRun">▶ Play Again</button>
      </div>
    </div>

    <hr class="tt-hr" />

    <!-- ── Question component ─────────────────────────────────────── -->
    <app-question
      v-if="showQuestion"
      :key="questionKey"
      :table="activeTable"
      :speed-mode="speedRunning"
      @correct="onCorrect"
      @wrong="onWrong"
    ></app-question>
  </div>
</template>

<script>
import Question from "./TimesTablesQuestion.vue";
import swal from "sweetalert";

const SPEED_SECONDS = 60;
const MODES = [
  { id: "practice", label: "Practice" },
  { id: "random",   label: "Random Mix" },
  { id: "speed",    label: "⚡ Speed Mode" },
];

export default {
  components: { appQuestion: Question },

  data() {
    return {
      MODES,
      SPEED_SECONDS,
      mode:          "practice",
      selectedTable: 2,

      // Speed Mode state
      speedRunning: false,
      speedDone:    false,
      speedCorrect: 0,
      speedWrong:   0,
      timeLeft:     SPEED_SECONDS,
      _ticker:      null,

      questionKey: 0,
    };
  },

  computed: {
    activeTable() {
      if (this.mode === "random" || this.mode === "speed") return 0; // 0 = random
      return this.selectedTable;
    },
    showQuestion() {
      if (this.mode === "speed") return this.speedRunning;
      return true;
    },
    speedScore() {
      return Math.max(0, this.speedCorrect * 10 - this.speedWrong * 3);
    },
  },

  methods: {
    setMode(id) {
      this.stopSpeedRun();
      this.mode = id;
      this.questionKey++;
    },

    selectTable(n) {
      this.selectedTable = n;
      this.questionKey++;
    },

    // ── Speed Mode ──────────────────────────────────────────────────
    startSpeedRun() {
      this.speedCorrect = 0;
      this.speedWrong   = 0;
      this.timeLeft     = SPEED_SECONDS;
      this.speedDone    = false;
      this.speedRunning = true;
      this.questionKey++;

      this._ticker = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) this.endSpeedRun();
      }, 1000);
    },

    endSpeedRun() {
      clearInterval(this._ticker);
      this.speedRunning = false;
      this.speedDone    = true;
    },

    stopSpeedRun() {
      clearInterval(this._ticker);
      this.speedRunning = false;
      this.speedDone    = false;
    },

    // ── Answer events from child ────────────────────────────────────
    onCorrect() {
      if (this.speedRunning) {
        this.speedCorrect++;
        // Auto-advance: bump key to mount a fresh question
        this.questionKey++;
      }
    },

    onWrong() {
      if (this.speedRunning) {
        this.speedWrong++;
      }
    },
  },

  beforeDestroy() {
    clearInterval(this._ticker);
  },
};
</script>

<style scoped>
.tt-wrapper {
  max-width: 580px;
  margin: 0 auto;
  padding: 24px 16px 60px;
  min-height: 65vh;
}

.tt-title {
  color: rgb(3, 53, 48);
  margin-bottom: 16px;
}

/* ── Mode bar ─────────────────────────────────────────────────────── */
.mode-bar {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.mode-btn {
  border-radius: 20px;
  padding: 5px 20px;
  font-size: 14px;
  cursor: pointer;
  background: transparent;
  color: rgb(3, 53, 48);
  border: 1.5px solid rgb(3, 53, 48);
  transition: all 0.18s;
  font-weight: 500;
}
.mode-btn:hover { background: rgb(169, 247, 234); }
.mode-btn--active {
  background: rgb(3, 53, 48);
  color: white;
  border-color: rgb(3, 53, 48);
}

/* ── Table picker ─────────────────────────────────────────────────── */
.table-picker { margin-bottom: 14px; }
.picker-label {
  font-size: 15px;
  color: rgb(3, 53, 48);
  display: block;
  margin-bottom: 8px;
}
.picker-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}
.tbl-btn {
  width: 46px;
  height: 36px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 1.5px solid rgb(3, 53, 48);
  transition: all 0.15s;
}
.tbl-btn:hover { background: rgb(100, 225, 205); }
.tbl-btn--active {
  background: rgb(3, 53, 48);
  color: white;
}

/* ── Mode description ─────────────────────────────────────────────── */
.mode-desc {
  font-size: 14px;
  color: #555;
  margin-bottom: 10px;
}

/* ── Speed HUD ────────────────────────────────────────────────────── */
.speed-controls { margin-bottom: 4px; }

.speed-hud {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 10px 0;
}
.hud-pill {
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 15px;
  font-weight: 700;
}
.hud-timer {
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 1.5px solid rgb(3, 53, 48);
  min-width: 70px;
  transition: background 0.3s, color 0.3s;
}
.hud-timer--low {
  background: rgb(220, 50, 50);
  color: white;
  border-color: rgb(180, 20, 20);
  animation: pulse 0.8s infinite;
}
.hud-correct {
  background: rgba(3, 120, 60, 0.15);
  color: rgb(3, 100, 50);
  border: 1.5px solid rgba(3, 120, 60, 0.4);
}
.hud-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 1.5px solid rgba(180, 30, 30, 0.3);
}

/* ── Speed results ────────────────────────────────────────────────── */
.speed-results { margin-top: 4px; }
.result-line {
  font-size: 20px;
  font-weight: 700;
  color: rgb(3, 53, 48);
  margin-bottom: 10px;
}
.result-stats {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.stat-pill {
  border-radius: 20px;
  padding: 5px 18px;
  font-size: 15px;
  font-weight: 600;
}
.stat-correct {
  background: rgba(3, 120, 60, 0.15);
  color: rgb(3, 100, 50);
  border: 1.5px solid rgba(3, 120, 60, 0.4);
}
.stat-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 1.5px solid rgba(180, 30, 30, 0.3);
}
.stat-score {
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 1.5px solid rgb(3, 53, 48);
}

/* ── Start button ─────────────────────────────────────────────────── */
.start-btn {
  background: rgb(3, 53, 48);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 32px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}
.start-btn:hover { background: rgb(5, 80, 70); }

.tt-hr {
  border-color: rgba(3, 53, 48, 0.2);
  margin: 14px 0 20px;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.05); }
}
</style>
